import {
    CoreSwitchView,
    CoreTextView,
    type TCoreCheckboxOptions,
    type TCoreCheckboxStyleProps,
    type TCoreInputData,
    type TCoreTextStyleProps,
} from '@core/widgets';
import { TWidget, TWidgetCommonOption } from '@libs/types';
import clsx from 'clsx';
import React, { ChangeEventHandler } from 'react';
import { getSemanticColors, memoByPalette, SemanticColor, TSemanticColors } from '../../theme/colors';
import { TextStyle } from '../../types/styles';
import styles from './checkBoxSwitcher.module.css';

const buildLabelStyleProps = (colors: TSemanticColors): TCoreTextStyleProps => ({
    ...TextStyle.main,
    main: colors[SemanticColor.TextSecondary],
    disabled: { main: colors[SemanticColor.TextPrimaryDisabled] },
    margin: { right: 24 },
});
const getLabelStyleProps = memoByPalette(buildLabelStyleProps);

const buildSwitchStyleProps = (colors: TSemanticColors): TCoreCheckboxStyleProps => ({
    width: 48,
    height: 28,
    borderStroke: 0,
    borderRadius: 14,
    thumbSize: 24,
    thumbSizeChecked: 24,
    thumbOffset: 2,
    thumbOffsetChecked: 2,
    main: colors[SemanticColor.FillPositive],
    mainContainer: colors[SemanticColor.IconPrimaryOnDarkFill],
    outline: colors[SemanticColor.IconPrimaryOnDarkFill],
    surfaceContainerHighest: colors[SemanticColor.BgTertiary],
    pressed: { main: colors[SemanticColor.FillPositivePressed], mainContainer: colors[SemanticColor.IconPrimaryOnDarkFill] },
    disabled: {
        main: colors[SemanticColor.FillPositiveDisabled],
        surfaceContainerHighest: colors[SemanticColor.BorderPrimary],
        outline: colors[SemanticColor.IconPrimaryOnDarkFill],
        mainContainer: colors[SemanticColor.IconPrimaryOnDarkFill],
    },
});
const getSwitchStyleProps = memoByPalette(buildSwitchStyleProps);

export type TSwitcherViewProps = {
    uid?: string;
    widget?: TWidget<TCoreInputData, TCoreCheckboxOptions>;
    label?: string;
    isChecked: boolean;
    handleChange: ChangeEventHandler<HTMLInputElement>;
    isDisabled?: boolean;
    isHide?: boolean;
    margin?: TWidgetCommonOption['margin'];
    style?: string;
    parentStyleNames?: string[];
    /** Design version of the widget it renders for (`TWidget.version`): picks semantic colors. */
    version: number | undefined;
};

export const SwitcherView = ({
    uid,
    widget,
    label,
    isChecked,
    handleChange,
    isDisabled,
    isHide,
    margin,
    style,
    parentStyleNames,
    version,
}: TSwitcherViewProps) => {
    const semanticColors = getSemanticColors(version);
    const switchWidget: TWidget<TCoreInputData, TCoreCheckboxOptions> | undefined = widget
        ? { ...widget, options: { ...widget.options, margin: undefined } }
        : undefined;

    const wrapperStyle: React.CSSProperties = {
        ...(isHide ? { display: 'none' } : null),
        ...(margin?.top !== undefined ? { marginTop: `${margin.top}px` } : null),
        ...(margin?.right !== undefined ? { marginRight: `${margin.right}px` } : null),
        ...(margin?.bottom !== undefined ? { marginBottom: `${margin.bottom}px` } : null),
        ...(margin?.left !== undefined ? { marginLeft: `${margin.left}px` } : null),
    };

    return (
        <div data-test={uid} style={wrapperStyle} className={clsx(styles.switchWrapper, isDisabled && styles.disabled)}>
            <div className={styles.switchInputBlock}>
                {label && <CoreTextView as="span" text={label} isDisabled={isDisabled} styleProps={getLabelStyleProps(semanticColors)} />}
                <CoreSwitchView
                    widget={switchWidget}
                    uid={uid}
                    isChecked={isChecked}
                    handleChange={handleChange}
                    isDisabled={isDisabled}
                    isHide={isHide}
                    styleName={style ?? 'switchDefault'}
                    parentStyleNames={parentStyleNames}
                    styleProps={getSwitchStyleProps(semanticColors)}
                />
            </div>
        </div>
    );
};
